"use client";

import type * as React from "react";
import { useState } from "react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { formatAud } from "@/lib/vehicles";
import {
  LucideChevronDown,
} from "lucide-react";

const labelClassName = "mb-1.5 block text-sm font-medium text-gray-900";
const optionalLabelClassName = "font-normal text-gray-500";

const inputClassName =
  "h-11 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 outline-none transition-colors placeholder:text-gray-400 focus:border-gray-500 focus:ring-1 focus:ring-gray-500";

const selectTriggerClassName =
  "h-11 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 shadow-none focus-visible:border-gray-500 focus-visible:ring-1 focus-visible:ring-gray-500";

const COUNTRY_OPTIONS = [
  { value: "AU", label: "Australia" },
  { value: "US", label: "United States" },
  { value: "GB", label: "United Kingdom" },
  { value: "NZ", label: "New Zealand" },
  { value: "SG", label: "Singapore" },
  { value: "IN", label: "India" },
] as const;

const PHONE_COUNTRY_OPTIONS = [
  { value: "+61", label: "🇦🇺 +61" },
  { value: "+1", label: "🇺🇸 +1" },
  { value: "+44", label: "🇬🇧 +44" },
  { value: "+64", label: "🇳🇿 +64" },
  { value: "+91", label: "🇮🇳 +91" },
] as const;

const AU_STATE_OPTIONS = [
  { value: "VIC", label: "Victoria" },
  { value: "NSW", label: "New South Wales" },
  { value: "QLD", label: "Queensland" },
  { value: "SA", label: "South Australia" },
  { value: "WA", label: "Western Australia" },
  { value: "TAS", label: "Tasmania" },
  { value: "NT", label: "Northern Territory" },
  { value: "ACT", label: "Australian Capital Territory" },
] as const;

type ConfirmFormSectionProps = {
  title: string;
  children: React.ReactNode;
};

const ConfirmFormSection: React.FC<ConfirmFormSectionProps> = ({
  title,
  children,
}) => (
  <section className="border-b border-gray-200 pb-8">
    <h3 className="text-xl font-bold text-gray-900">{title}</h3>
    <div className="mt-5 space-y-4">{children}</div>
  </section>
);

type FormFieldProps = {
  label: string;
  optional?: boolean;
  children: React.ReactNode;
  className?: string;
};

const FormField: React.FC<FormFieldProps> = ({
  label,
  optional = false,
  children,
  className,
}) => (
  <div className={className}>
    <label className={labelClassName}>
      {label}
      {optional ? (
        <span className={optionalLabelClassName}> (optional)</span>
      ) : null}
    </label>
    {children}
  </div>
);

type BookingConfirmFormProps = {
  bookingTotal: number | null;
  isTotalLoading: boolean;
  onPriceDetailsClick: () => void | Promise<void>;
};

export const BookingConfirmForm: React.FC<BookingConfirmFormProps> = ({
  bookingTotal,
  isTotalLoading,
  onPriceDetailsClick,
}) => {

  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phoneCountry, setPhoneCountry] = useState("+61");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [company, setCompany] = useState("");
  const [isOver25, setIsOver25] = useState(false);

  const [invoiceCountry, setInvoiceCountry] = useState("AU");
  const [streetAddress, setStreetAddress] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");

  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showImportantInfo, setShowImportantInfo] = useState(false);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
  };

  return (
    <form className="flex flex-col gap-8" onSubmit={handleSubmit}>
        <ConfirmFormSection title="Who will drive?">
          <FormField label="Email">
            <input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              className={inputClassName}
              autoComplete="email"
              placeholder="you@example.com"
            />
          </FormField>

          <div className="grid gap-4 sm:grid-cols-2">
            <FormField label="First name">
              <input
                type="text"
                value={firstName}
                onChange={(event) => setFirstName(event.target.value)}
                className={inputClassName}
                autoComplete="given-name"
                placeholder="John"
              />
            </FormField>
            <FormField label="Last name">
              <input
                type="text"
                value={lastName}
                onChange={(event) => setLastName(event.target.value)}
                className={inputClassName}
                autoComplete="family-name"
                placeholder="Smith"
              />
            </FormField>
          </div>

          <div className="grid gap-4 sm:grid-cols-[140px_minmax(0,1fr)]">
            <FormField label="Country">
              <Select
                value={phoneCountry}
                onValueChange={(value) => {
                  if (value) {
                    setPhoneCountry(value);
                  }
                }}
              >
                <SelectTrigger className={selectTriggerClassName}>
                  <SelectValue placeholder="Select code" />
                </SelectTrigger>
                <SelectContent>
                  {PHONE_COUNTRY_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Phone number">
              <input
                type="tel"
                value={phoneNumber}
                onChange={(event) => setPhoneNumber(event.target.value)}
                className={inputClassName}
                autoComplete="tel-national"
                placeholder="412 345 678"
              />
            </FormField>
          </div>

          <FormField label="Company" optional>
            <input
              type="text"
              value={company}
              onChange={(event) => setCompany(event.target.value)}
              className={inputClassName}
              autoComplete="organization"
              placeholder="Company name"
            />
          </FormField>

          <label className="flex cursor-pointer items-start gap-3">
            <input
              type="checkbox"
              checked={isOver25}
              onChange={(event) => setIsOver25(event.target.checked)}
              className="mt-0.5 size-4 rounded border-gray-300 text-gray-900 focus:ring-gray-500"
            />
            <span className="text-sm text-gray-800">
              I am 25 years of age or older
            </span>
          </label>
        </ConfirmFormSection>

        <ConfirmFormSection title="What's your invoice address?">
          <FormField label="Country">
            <Select
              value={invoiceCountry}
              onValueChange={(value) => {
                if (value) {
                  setInvoiceCountry(value);
                }
              }}
            >
              <SelectTrigger className={selectTriggerClassName}>
                <SelectValue placeholder="Select country" />
              </SelectTrigger>
              <SelectContent>
                {COUNTRY_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>

          <FormField label="Street address">
            <input
              type="text"
              value={streetAddress}
              onChange={(event) => setStreetAddress(event.target.value)}
              className={inputClassName}
              autoComplete="street-address"
              placeholder="123 Example Street"
            />
          </FormField>

          <div className="grid gap-4 sm:grid-cols-2">
            <FormField label="Zip">
              <input
                type="text"
                value={zipCode}
                onChange={(event) => setZipCode(event.target.value)}
                className={inputClassName}
                autoComplete="postal-code"
                placeholder="3175"
              />
            </FormField>
            <FormField label="City">
              <input
                type="text"
                value={city}
                onChange={(event) => setCity(event.target.value)}
                className={inputClassName}
                autoComplete="address-level2"
                placeholder="Melbourne"
              />
            </FormField>
          </div>

          <FormField label="State">
            <Select
              value={state}
              onValueChange={(value) => {
                if (value) {
                  setState(value);
                }
              }}
            >
              <SelectTrigger className={selectTriggerClassName}>
                <SelectValue placeholder="Select state" />
              </SelectTrigger>
              <SelectContent>
                {AU_STATE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>
        </ConfirmFormSection>

        <section className="space-y-5">
          <div>
            <p className="text-sm text-gray-600">Total</p>
            <div className="mt-1 flex flex-wrap items-end gap-3">
              {isTotalLoading ? (
                <span className="text-3xl font-bold text-gray-900">—</span>
              ) : (
                <span className="text-3xl font-bold text-gray-900">
                  AU$ {formatAud(bookingTotal ?? 0)}
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={() => void onPriceDetailsClick()}
              className="mt-1 cursor-pointer text-sm text-gray-500 underline-offset-2 hover:underline"
            >
              Price details
            </button>
          </div>

          <button
            type="button"
            onClick={() => setShowImportantInfo((current) => !current)}
            className="flex w-full items-center justify-between rounded-lg border border-gray-200 bg-white px-4 py-3 text-left text-sm font-medium text-gray-900"
          >
            Important information about your reservation
            <LucideChevronDown
              size={18}
              className={cn(
                "text-gray-500 transition-transform",
                showImportantInfo && "rotate-180",
              )}
            />
          </button>

          {showImportantInfo ? (
            <div className="rounded-lg bg-slate-100 px-4 py-3 text-sm text-gray-700">
              Please bring a valid driver&apos;s licence and the credit card
              used for payment when collecting your vehicle. Cancellation and
              rebooking are free within 24 hours of booking.
            </div>
          ) : null}

          <label className="flex cursor-pointer items-start gap-3">
            <input
              type="checkbox"
              checked={acceptedTerms}
              onChange={(event) => setAcceptedTerms(event.target.checked)}
              className="mt-0.5 size-4 rounded border-gray-300 text-gray-900 focus:ring-gray-500"
            />
            <span className="text-sm text-gray-800">
              I confirm that I have read and accepted the{" "}
              <Link href="#" className="font-medium underline underline-offset-2">
                Rental Information
              </Link>
              ,{" "}
              <Link href="#" className="font-medium underline underline-offset-2">
                Terms and Conditions
              </Link>{" "}
              and{" "}
              <Link href="#" className="font-medium underline underline-offset-2">
                Privacy Policy
              </Link>
              .
            </span>
          </label>

          <Button
            type="submit"
            size="xl"
            className="w-100"
            // className="h-14 w-full bg-orange-500 text-lg font-semibold hover:bg-orange-600"
            disabled={!acceptedTerms}
          >
            Reserve
          </Button>
        </section>
      </form>
  );
};
